import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Modal } from "../ui-bits";
import { eur } from "./common";

const n = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });

export function ProductionRunModal({ sheet, garment, onClose, onDone }) {
  const sizes = garment.sizes?.length ? garment.sizes : ["Unica"];
  const colors = garment.colors?.length ? garment.colors : ["Unico"];
  const [mode, setMode] = useState("total");
  const [pieces, setPieces] = useState("");
  const [grid, setGrid] = useState({});
  const [plan, setPlan] = useState(null);
  const [qty, setQty] = useState({});
  const [sup, setSup] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [expected, setExpected] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  useEffect(() => { api.get("/suppliers").then(r => setSuppliers(r.data)).catch(() => {}); }, []);
  const breakdown = mode === "grid" ? Object.entries(grid).filter(([, q]) => Number(q) > 0).map(([k, q]) => { const [size, color] = k.split("|"); return { size, color, qty: Number(q) }; }) : [];
  const total = mode === "grid" ? breakdown.reduce((a, r) => a + r.qty, 0) : Number(pieces) || 0;

  useEffect(() => {
    if (total <= 0) { setPlan(null); return; }
    let alive = true;
    const t = setTimeout(() => api.post(`/sampling/production-sheets/${sheet.id}/purchase-plan`, mode === "grid" ? { breakdown } : { pieces: total })
      .then(r => { if (!alive) return; setPlan(r.data);
        setQty(Object.fromEntries(r.data.rows.map(x => [x.material_id, String(x.to_order || 0)])));
        setSup(s => Object.fromEntries(r.data.rows.map(x => [x.material_id, s[x.material_id] ?? (x.supplier_id || "")]))); })
      .catch(() => alive && setErr("Calcolo non riuscito")), 350);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, mode, JSON.stringify(breakdown), sheet.id]);

  const lines = (plan?.rows || []).filter(r => Number(qty[r.material_id]) > 0).map(r => ({ material_id: r.material_id, quantity: Number(qty[r.material_id]), supplier_id: sup[r.material_id], unit_cost: r.unit_cost }));
  const noSupplier = lines.some(l => !l.supplier_id);
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/sampling/production-sheets/${sheet.id}/purchase-orders`, { ...(mode === "grid" ? { breakdown } : { pieces: total }), lines, expected_date: expected || null });
      setDone(r.data); onDone?.();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Modal open onClose={onClose} title={`Ordini materiali · ${sheet.number}`} wide>
      {done ? (
        <div data-testid="run-done" className="text-sm space-y-3">
          <p className="text-green-700">Creati {done.purchase_orders.length} ordini ai fornitori per {done.run.pieces} pezzi:</p>
          <ul className="list-disc pl-5">{done.purchase_orders.map(p => <li key={p.id}><b>{p.number}</b> · {p.supplier_name} · {eur(p.total)}</li>)}</ul>
          {done.production && <p>Creata la produzione <a href={`/productions?open=${done.production.id}`} className="underline font-medium" data-testid="run-done-production">{done.production.number}</a>: seguine l'avanzamento nella pagina Produzione.</p>}
          <p className="text-xs opacity-60">Li trovi in Materiali &amp; Magazzino → Ordini fornitori. La merce risulta "in arrivo".</p>
          <button onClick={onClose} className="btn-gold" data-testid="run-close">Chiudi</button>
        </div>
      ) : (
        <div data-testid="run-modal">
          <div className="flex gap-2 mb-4">
            {[["total", "Pezzi totali"], ["grid", "Per taglia e colore"]].map(([k, l]) => (
              <button key={k} onClick={() => setMode(k)} className={k === mode ? "btn-gold" : "btn-ghost"} data-testid={`run-mode-${k}`}>{l}</button>))}
          </div>
          {mode === "total" ? (
            <div className="max-w-xs mb-4"><label className="label-caps block mb-2">Pezzi da produrre</label>
              <input type="number" min="0" className="input-dark" value={pieces} onChange={e => setPieces(e.target.value)} data-testid="run-pieces"/></div>
          ) : (
            <div className="overflow-x-auto mb-4"><table className="tbl text-xs" data-testid="run-grid">
              <thead><tr><th>Colore / Taglia</th>{sizes.map(s => <th key={s} className="text-right">{s}</th>)}</tr></thead>
              <tbody>{colors.map(c => <tr key={c}><td>{c}</td>{sizes.map(s => (
                <td key={s} className="text-right"><input type="number" min="0" className="input-dark !py-1 !w-16 text-right" value={grid[`${s}|${c}`] || ""}
                  onChange={e => setGrid({ ...grid, [`${s}|${c}`]: e.target.value })} data-testid={`run-grid-${s}-${c}`}/></td>))}</tr>)}</tbody>
            </table></div>
          )}
          <div className="text-sm mb-4" data-testid="run-total">Totale pezzi: <b>{total}</b></div>
          {plan && (
            <div className="overflow-x-auto"><table className="tbl w-full text-xs [&_td]:!px-2 [&_th]:!px-2" data-testid="run-plan">
              <thead><tr><th>Materiale</th><th className="text-right">Serve</th><th className="text-right">Libero</th><th className="text-right">In arrivo</th><th className="text-right">Da ordinare</th><th>Fornitore</th></tr></thead>
              <tbody>{plan.rows.map(r => (
                <tr key={r.material_id} data-testid={`run-row-${r.material_id}`}>
                  <td>{r.name}</td><td className="text-right whitespace-nowrap">{n(r.need)} {r.unit}</td><td className="text-right">{n(r.free)}</td><td className="text-right">{n(r.incoming)}</td>
                  <td className="text-right"><input type="number" min="0" step="0.01" className="input-dark !py-1 !w-24 text-right" value={qty[r.material_id] ?? ""} onChange={e => setQty({ ...qty, [r.material_id]: e.target.value })} data-testid={`run-qty-${r.material_id}`}/></td>
                  <td><select className="input-dark !py-1 text-xs" value={sup[r.material_id] || ""} onChange={e => setSup({ ...sup, [r.material_id]: e.target.value })} data-testid={`run-supplier-${r.material_id}`}>
                    <option value="">—</option>{suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></td>
                </tr>))}</tbody>
            </table></div>
          )}
          <div className="max-w-xs mt-4"><label className="label-caps block mb-2">Consegna prevista</label>
            <input type="date" className="input-dark" value={expected} onChange={e => setExpected(e.target.value)} data-testid="run-expected"/></div>
          {plan && plan.rows.every(r => r.to_order === 0) && <div className="text-sm text-green-700 mt-3" data-testid="run-covered">Tutto coperto da magazzino e merce in arrivo: non serve ordinare nulla.</div>}
          {noSupplier && <div className="text-xs text-orange-700 mt-3">Scegli un fornitore per ogni materiale da ordinare.</div>}
          {err && <div className="text-sm text-red-600 mt-3" data-testid="run-error">{err}</div>}
          <div className="flex justify-between items-center mt-6">
            <span className="text-sm">{lines.length} materiali · {eur(lines.reduce((a, l) => a + l.quantity * (l.unit_cost || 0), 0))}</span>
            <button disabled={busy || !lines.length || noSupplier || total <= 0} onClick={save} className="btn-gold" data-testid="run-save">{busy ? "Creazione…" : "Crea ordini ai fornitori"}</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
